import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Chip, Typography, Tabs, Tab, Table, TableBody, TableRow, TableCell,
  Button, Paper, TableHead, Link, Dialog, DialogTitle, DialogContent, DialogActions,
  Autocomplete, TextField
} from '@mui/material';
import api from '../api';
import dayjs from 'dayjs';
import ProjectMemberList from '../components/ProjectMemberList';
import ProjectChat from '../components/ProjectChat';
import InvoiceCreateDialog from "../components/invoices/InvoiceCreateDialog";
import EstimateCreateDialog from "../components/estimates/EstimateCreateDialog";
import ProjectWorkloadPanel from '../components/ProjectWorkloadPanel';
import PageHeader from '../components/common/PageHeader';
import PageContainer from '../components/common/PageContainer';
import { issueDocumentPdf } from '../lib/documentPdf';

export default function ProjectDetail() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [tab, setTab] = useState(0);
  const [workSums, setWorkSums] = useState({});
  const [loading, setLoading] = useState(false);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [openInvoiceEdit, setOpenInvoiceEdit] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [deliveryLoading, setDeliveryLoading] = useState(false);
  const [openDelivery, setOpenDelivery] = useState(false);
  const [deliveryForm, setDeliveryForm] = useState({ issueDate: '', amount: '', note: '' });
  const [orderLoading, setOrderLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [workloads, setWorkloads] = useState([]);
  const [workloadLoading, setWorkloadLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [files, setFiles] = useState([]);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [payments, setPayments] = useState([]);
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ payeeId: null, amount: '', note: '' });
  const [outsources, setOutsources] = useState([]);
  const [selectedPayee, setSelectedPayee] = useState(null);
  const [reviseLoading, setReviseLoading] = useState(false);

  // 請求書（新規・複製）
  const [openInvoice, setOpenInvoice] = useState(false);
  const [invoiceCustomer, setInvoiceCustomer] = useState(null);
  const [invoiceProjectId, setInvoiceProjectId] = useState(null);
  const [invoiceInitialData, setInvoiceInitialData] = useState(null);

  // 見積書（新規・複製・修正）
  const [openEstimate, setOpenEstimate] = useState(false);
  const [estimateCustomer, setEstimateCustomer] = useState(null);
  const [estimateProjectId, setEstimateProjectId] = useState(null);
  const [estimateInitialData, setEstimateInitialData] = useState(null);
  const [openEstimateEdit, setOpenEstimateEdit] = useState(false);
  const [editingEstimate, setEditingEstimate] = useState(null);

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/api/outsources');
        const list =
          Array.isArray(data) ? data
            : Array.isArray(data.data) ? data.data
              : Array.isArray(data.outsources) ? data.outsources
                : [];
        setOutsources(list);
      } catch (err) {
        console.error('[外注先一覧取得エラー]', err);
      }
    })();
  }, []);

  // --- 支払登録 (新規 or 編集、xlsx 行70) ---
  const [editPaymentId, setEditPaymentId] = React.useState(null);

  const openCreatePayment = () => {
    setEditPaymentId(null);
    setSelectedPayee(null);
    setPaymentForm({ payeeId: null, amount: '', note: '' });
    setOpenPaymentDialog(true);
  };

  const openEditPayment = (p) => {
    setEditPaymentId(p.id);
    setSelectedPayee(outsources.find(o => String(o.id) === String(p.payeeId)) || null);
    setPaymentForm({
      payeeId: p.payeeId,
      amount: p.amount,
      note: p.note || '',
    });
    setOpenPaymentDialog(true);
  };

  const handleSavePayment = async () => {
    if (!paymentForm.payeeId || !paymentForm.amount) {
      alert('支払先と金額は必須です');
      return;
    }
    try {
      const payload = {
        projectId: Number(id),
        payeeId: Number(paymentForm.payeeId),
        amount: Number(paymentForm.amount),
        note: paymentForm.note || null,
        status: editPaymentId ? undefined : '起案',
        expenseType: 'project',
      };
      if (editPaymentId) {
        await api.put(`/api/payments/${editPaymentId}`, payload);
      } else {
        await api.post('/api/payments', { ...payload, status: '起案' });
      }
      await loadPayments();
      setOpenPaymentDialog(false);
      setEditPaymentId(null);
      setSelectedPayee(null);
      setPaymentForm({ payeeId: null, amount: '', note: '' });
    } catch (err) {
      console.error('[支払登録エラー]', err);
      alert(`支払${editPaymentId ? '更新' : '登録'}に失敗しました: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('この支払登録を削除しますか？')) return;
    try {
      await api.delete(`/api/payments/${paymentId}`);
      await loadPayments();
    } catch (err) {
      alert(`削除できませんでした: ${err.response?.data?.error || err.message}`);
    }
  };

  // backward-compat (旧 handleCreatePayment 参照向け)
  const handleCreatePayment = handleSavePayment;

  // --- 合計算出 ---
  const paymentSummary = React.useMemo(() => {
    const total = payments.reduce((s, p) => s + (p.amount || 0), 0);
    const approved = payments
      .filter(p => p.status === '承認済')
      .reduce((s, p) => s + (p.amount || 0), 0);
    const pending = payments
      .filter(p => p.status === '承認待ち')
      .reduce((s, p) => s + (p.amount || 0), 0);
    const rejected = payments
      .filter(p => p.status === '却下')
      .reduce((s, p) => s + (p.amount || 0), 0);
    return { total, approved, pending, rejected };
  }, [payments]);

  // --- 案件取得 ---
  useEffect(() => {
    api.get(`/api/projects/${id}`).then(res => setProject(res.data));
  }, [id]);

  // --- 発行履歴 (xlsx 行18 + Batch 2.11): Log ベース、PDF発行ごとに1行 ---
  const loadHistory = async () => {
    try {
      const res = await api.get(`/api/projects/${id}/issue-history`);
      setHistory(res.data || []);
    } catch (err) {
      console.error('[発行履歴取得エラー]', err);
      setHistory([]);
    }
  };
  useEffect(() => {
    if (tab === 6) loadHistory();
  }, [tab]);

  // =========================
  // 請求書：修正 / 複製
  // =========================
  const handleReviseInvoice = async (invoiceId) => {
    if (!invoiceId) return;

    const ok = window.confirm(
      'この請求書を修正しますか？\n修正すると現在の版は無効化され、新しい版が作成されます。'
    );
    if (!ok) return;

    try {
      setReviseLoading(true);

      const { data } = await api.get(`/api/invoices/${invoiceId}`);

      const payload = {
        customer_id: data.customerId,
        project_id: data.projectId,
        invoice: {
          invoice_date: data.issueDate ? dayjs(data.issueDate).format('YYYY-MM-DD') : null,
          due_date: data.dueDate ? dayjs(data.dueDate).format('YYYY-MM-DD') : null,
          notes: data.note || '',
          title: data.title ?? '',
        },
        items: Array.isArray(data.items) ? data.items.map((it) => ({
          description: it.description,
          quantity: it.quantity,
          unit: it.unit,
          unit_price: it.unitPrice,
          tax_rate: it.taxRate,
          amount: it.amount,
        })) : [],
        totals: {
          total: data.amount || 0,
        },
      };

      await api.post(`/api/invoices/${invoiceId}/revise`, payload);

      alert('請求書を修正しました。');
      await loadHistory();
    } catch (err) {
      console.error('[請求書修正エラー]', err);
      alert('請求書の修正に失敗しました。');
    } finally {
      setReviseLoading(false);
    }
  };

  const handleCopyInvoice = async (invoiceId) => {
    if (!invoiceId) return;

    try {
      const { data } = await api.get(`/api/invoices/${invoiceId}`);

      setInvoiceCustomer({
        id: data.customerId,
        name: data.customerName,
      });

      setInvoiceProjectId(data.projectId);

      setInvoiceInitialData({
        projectId: data.projectId,
        title: data.title ?? '',
        items: Array.isArray(data.items) ? data.items : [],
        issueDate: dayjs().format('YYYY-MM-DD'),
        dueDate: '',
      });

      setOpenInvoice(true);
    } catch (err) {
      console.error('[請求書複製エラー]', err);
      alert('請求書の複製に失敗しました。');
    }
  };

  const handleEditInvoice = async (invoiceId) => {
    try {
      const { data } = await api.get(`/api/invoices/${invoiceId}`);
      setEditingInvoice(data);
      setOpenInvoiceEdit(true);
    } catch (err) {
      console.error("[Invoice load error]", err);
      alert("請求書の取得に失敗しました");
    }
  };

  // =========================
  // 見積書：修正 / 複製 / 削除
  // =========================
  const handleEditEstimate = async (estimateId) => {
    try {
      const { data } = await api.get(`/api/estimates/${estimateId}`);
      setEditingEstimate(data);
      setOpenEstimateEdit(true);
    } catch (err) {
      console.error("[Estimate load error]", err);
      alert("見積書の取得に失敗しました");
    }
  };

  const handleCopyEstimate = async (estimateId) => {
    if (!estimateId) return;
    try {
      const { data } = await api.get(`/api/estimates/${estimateId}`);

      setEstimateCustomer({
        id: data.customerId,
        name: data.customerName,
      });

      setEstimateProjectId(data.projectId);

      setEstimateInitialData({
        project_id: data.projectId,
        customer_id: data.customerId,
        estimate: {
          title: data.title ?? '',
          estimate_date: dayjs().format('YYYY-MM-DD'),
          expire_date: '',
          notes: data.note || '',
        },
        items: Array.isArray(data.items) ? data.items.map(it => ({
          description: it.description,
          quantity: it.quantity,
          unit: it.unit,
          unit_price: it.unitPrice,
          tax_rate: it.taxRate,
          amount: it.amount,
        })) : [],
        totals: {
          total: data.amount || 0,
        },
      });


      setOpenEstimate(true);
    } catch (err) {
      console.error('[見積書複製エラー]', err);
      alert('見積書の複製に失敗しました。');
    }
  };

  const handleDeleteEstimate = async (estimateId) => {
    if (!estimateId) return;
    const ok = window.confirm('この見積書を削除しますか？');
    if (!ok) return;

    try {
      await api.delete(`/api/estimates/${estimateId}`);
      await loadHistory();
      alert('見積書を削除しました。');
    } catch (err) {
      console.error('[見積書削除エラー]', err);
      alert('見積書の削除に失敗しました。');
    }
  };

  // --- 支払データ ---
  const loadPayments = async () => {
    try {
      const { data } = await api.get(`/api/payments?projectId=${id}`);
      setPayments(data.data || data);
    } catch (err) {
      console.error('[支払データ取得エラー]', err);
    }
  };

  useEffect(() => {
    if (tab === 3) loadPayments();
  }, [tab]);

  // --- 受注データ ---
  const loadOrders = async () => {
    try {
      const { data } = await api.get(`/api/orders?projectId=${id}`);
      setOrders(data.data || data);
    } catch (err) {
      console.error('[受注データ取得エラー]', err);
    }
  };
  useEffect(() => {
    if (tab === 1) loadOrders();
  }, [tab]);

  // --- 工数データ ---
  const loadWorkloads = async () => {
    try {
      setWorkloadLoading(true);
      const { data } = await api.get(`/api/workloads?projectId=${id}`);
      setWorkloads(data.data || data);
    } catch (err) {
      console.error('[工数データ取得エラー]', err);
    } finally {
      setWorkloadLoading(false);
    }
  };
  useEffect(() => {
    if (tab === 2) loadWorkloads();
  }, [tab]);

  // --- 契約データ ---
  const loadContracts = async () => {
    try {
      const { data } = await api.get(`/api/contracts?projectId=${id}`);
      setContracts(data.data || data);
    } catch (err) {
      console.error('[契約データ取得エラー]', err);
    }
  };
  useEffect(() => {
    if (tab === 4) loadContracts();
  }, [tab]);

  // --- ファイル一覧 ---
  const loadFiles = async () => {
    try {
      const { data } = await api.get(`/api/files?projectId=${id}`);
      const list =
        Array.isArray(data)
          ? data
          : Array.isArray(data.data)
            ? data.data
            : Array.isArray(data.files)
              ? data.files
              : [];
      setFiles(list);
    } catch (err) {
      console.error('[ファイル一覧取得エラー]', err);
    }
  };
  useEffect(() => {
    if (tab === 5) loadFiles();
  }, [tab]);

  // --- ファイルアップロード ---
  const handleUploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const formData = new FormData();
      const utf8Name = new Blob([file], { type: file.type });
      formData.append('file', utf8Name, file.name);
      formData.append('related_type', 'project');
      formData.append('related_id', id);
      formData.append('file_type', file.type.split('/').pop() || 'other');
      formData.append('version', 1);
      await api.post('/api/files', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await loadFiles();
    } catch (err) {
      alert('アップロードに失敗しました。');
      console.error('[ファイルアップロードエラー]', err);
    }
  };

  // --- ファイル削除 ---
  const handleDeleteFile = async () => {
    if (!deleteDialog) return;
    try {
      await api.delete(`/api/files/${deleteDialog.id}`);
      await loadFiles();
    } catch (err) {
      alert('削除に失敗しました。');
      console.error('[ファイル削除エラー]', err);
    } finally {
      setDeleteDialog(null);
    }
  };

  // --- 支払承認／却下 ---
  const handleApprove = async (paymentId) => {
    if (!window.confirm('この支払いを承認しますか？')) return;
    await api.post(`/api/payments/${paymentId}/approve`);
    await loadPayments();
  };

  const handleReject = async (paymentId) => {
    if (!window.confirm('この支払いを却下しますか？')) return;
    await api.post(`/api/payments/${paymentId}/reject`);
    await loadPayments();
  };

  // --- 受注書発行 (xlsx 行93) ---
  const handleCreateOrder = async () => {
    try {
      if (!project?.id) return alert('案件データが未取得です');
      if (!project?.customerId) {
        return alert('この案件にはクライアントが設定されていないため受注書を発行できません');
      }
      setOrderLoading(true);
      const payload = {
        projectId: Number(project.id),
        customerId: Number(project.customerId),
        title: project.title || '',
        amount: project.totalCost ?? 0,
        orderDate: dayjs().format('YYYY-MM-DD'),
        status: '発行済',
      };
      const { data: order } = await api.post('/api/orders', payload);
      await issueDocumentPdf('orders', order?.id);
      await loadOrders();
      await loadHistory();
    } catch (err) {
      console.error('[受注発行エラー]', err);
      alert(`受注書の発行に失敗しました: ${err.response?.data?.error || err.message}`);
    } finally {
      setOrderLoading(false);
    }
  };

  // --- 納品書発行 (xlsx 行92: 発行日を任意に指定できるようにする) ---
  const openDeliveryDialog = () => {
    if (!project?.id) return alert('案件データが未取得です');
    setDeliveryForm({
      issueDate: dayjs().format('YYYY-MM-DD'),
      amount: project.totalCost != null ? String(project.totalCost) : '0',
      note: project.description || '',
    });
    setOpenDelivery(true);
  };

  const handleCreateDelivery = async () => {
    if (!deliveryForm.issueDate) {
      alert('発行日を入力してください');
      return;
    }
    try {
      setDeliveryLoading(true);
      const { data: del } = await api.post('/api/deliveries', {
        projectId: Number(project.id),
        customerId: project.customerId ? Number(project.customerId) : null,
        amount: deliveryForm.amount !== '' ? Number(deliveryForm.amount) : 0,
        issueDate: deliveryForm.issueDate,
        status: '発行済',
        note: deliveryForm.note || null,
      });
      await issueDocumentPdf('deliveries', del?.id);
      setOpenDelivery(false);
      await loadHistory();
    } catch (err) {
      console.error('[納品書発行エラー]', err);
      alert(`納品書の発行に失敗しました: ${err.response?.data?.error || err.message}`);
    } finally {
      setDeliveryLoading(false);
    }
  };

  if (!project) return <div>Loading...</div>;

  return (
    <PageContainer>
      <Box sx={{ width: '100%' }}>
        <PageHeader
          title="案件詳細"
          actions={
            <>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  if (!project || !project.customerId) {
                    alert('この案件にはクライアントが設定されていません');
                    return;
                  }
                  setEstimateCustomer({
                    id: project.customerId,
                    name: project.customerName,
                  });
                  setEstimateProjectId(project.id);
                  setEstimateInitialData(null);
                  setOpenEstimate(true);
                }}
                disabled={loading}
              >
                見積書新規発行
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  if (!project || !project.customerId) {
                    alert('この案件にはクライアントが設定されていません');
                    return;
                  }
                  setInvoiceCustomer({
                    id: project.customerId,
                    name: project.customerName,
                  });
                  setInvoiceProjectId(project.id);
                  setInvoiceInitialData(null);
                  setOpenInvoice(true);
                }}
                disabled={invoiceLoading}
              >
                請求書新規発行
              </Button>
              <Button
                variant="contained"
                onClick={openDeliveryDialog}
                disabled={deliveryLoading}
              >
                {deliveryLoading ? '発行中...' : '納品書発行'}
              </Button>
            </>
          }
        />

        {/* --- 案件概要 --- */}
        <Paper sx={{ mb: 3, p: 2 }}>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>案件名</TableCell>
                <TableCell>{project.title}</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>顧客</TableCell>
                <TableCell>{project.customerName}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>案件金額</TableCell>
                <TableCell>{project.totalCost != null ? `¥${Number(project.totalCost).toLocaleString()}` : '-'}</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>ステータス</TableCell>
                <TableCell>{project.statusLabel || project.status}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>外注費合計</TableCell>
                <TableCell>¥{paymentSummary.total.toLocaleString()}</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>承認済 / 承認待ち</TableCell>
                <TableCell>
                  <span style={{ color: '#2e7d32' }}>¥{paymentSummary.approved.toLocaleString()}</span>
                  {' / '}
                  <span style={{ color: '#0288d1' }}>¥{paymentSummary.pending.toLocaleString()}</span>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Paper>

        {/* --- タブ --- */}
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label="メンバー" />
          <Tab label="受注" />
          <Tab label="工数" />
          <Tab label="外注費" />
          <Tab label="契約" />
          <Tab label="ファイル" />
          <Tab label="発行履歴" />
          <Tab label="チャット" />
        </Tabs>

        {/* --- メンバー --- */}
        {tab === 0 && (
          <ProjectMemberList projectId={id} workSums={workSums} setWorkSums={setWorkSums} />
        )}

        {/* --- 受注 --- */}
        {tab === 1 && (
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="h6">受注履歴</Typography>
              <Button variant="contained" onClick={handleCreateOrder} disabled={orderLoading}>
                {orderLoading ? '発行中...' : '受注書発行'}
              </Button>
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>受注日</TableCell>
                  <TableCell>クライアント</TableCell>
                  <TableCell align="right">金額</TableCell>
                  <TableCell>ステータス</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.map((o) => (
                  <TableRow key={o.id}>
                    <TableCell>{o.orderDate ? dayjs(o.orderDate).format('YYYY/MM/DD') : '-'}</TableCell>
                    <TableCell>{o.customerName}</TableCell>
                    <TableCell align="right">¥{(o.amount || 0).toLocaleString()}</TableCell>
                    <TableCell>
                      <Chip label={o.status || '-'} color="info" size="small" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}

        {/* --- 工数 --- */}
        {tab === 2 && <ProjectWorkloadPanel projectId={id} />}

        {/* --- 支払 --- */}
        {tab === 3 && (
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">外注費一覧</Typography>
              <Button variant="contained" onClick={openCreatePayment}>
                外注費登録
              </Button>
            </Box>

            <Box sx={{ display: 'flex', gap: 3, mb: 2, fontWeight: 'bold' }}>
              <span>合計: ¥{paymentSummary.total.toLocaleString()}</span>
              <span style={{ color: '#2e7d32' }}>承認済: ¥{paymentSummary.approved.toLocaleString()}</span>
              <span style={{ color: '#0288d1' }}>承認待ち: ¥{paymentSummary.pending.toLocaleString()}</span>
              <span style={{ color: '#d32f2f' }}>却下: ¥{paymentSummary.rejected.toLocaleString()}</span>
            </Box>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>支払先</TableCell>
                  <TableCell align="right">金額</TableCell>
                  <TableCell>ステータス</TableCell>
                  <TableCell>登録日</TableCell>
                  <TableCell>承認日</TableCell>
                  <TableCell align="center">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>{p.payeeName || p.payee?.name || '-'}</TableCell>
                    <TableCell align="right">¥{(p.amount || 0).toLocaleString()}</TableCell>
                    <TableCell>
                      <Chip
                        label={p.status || '-'}
                        color={
                          p.status === '承認済'
                            ? 'success'
                            : p.status === '却下'
                              ? 'error'
                              : 'default'
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{p.requestedAt ? dayjs(p.requestedAt).format('YYYY/MM/DD') : '-'}</TableCell>
                    <TableCell>{p.approvedAt ? dayjs(p.approvedAt).format('YYYY/MM/DD') : '-'}</TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'inline-flex', gap: 0.5, alignItems: 'center' }}>
                        {p.status === '承認待ち' && (
                          <>
                            <Button size="small" color="success" onClick={() => handleApprove(p.id)}>
                              承認
                            </Button>
                            <Button size="small" color="error" onClick={() => handleReject(p.id)}>
                              却下
                            </Button>
                          </>
                        )}
                        {/* xlsx 行70: 編集/削除 */}
                        <Button size="small" variant="outlined" onClick={() => openEditPayment(p)}>
                          編集
                        </Button>
                        <Button size="small" variant="outlined" color="error" onClick={() => handleDeletePayment(p.id)}>
                          削除
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Dialog open={openPaymentDialog} onClose={() => setOpenPaymentDialog(false)} fullWidth maxWidth="md">
              <DialogTitle>{editPaymentId ? '支払を編集' : '支払登録'}</DialogTitle>
              <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                <Autocomplete
                  options={outsources}
                  getOptionLabel={(o) => o?.name || ''}
                  isOptionEqualToValue={(o, v) => String(o?.id) === String(v?.id)}
                  value={selectedPayee}
                  onChange={(_, val) => {
                    setSelectedPayee(val);
                    setPaymentForm({ ...paymentForm, payeeId: val ? val.id : null });
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="支払先（外注先）" placeholder="外注先を選択" />
                  )}
                />
                <TextField
                  label="金額（円）"
                  type="number"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  inputProps={{ min: 0 }}
                />
                <TextField
                  label="備考"
                  multiline
                  rows={3}
                  value={paymentForm.note}
                  onChange={(e) => setPaymentForm({ ...paymentForm, note: e.target.value })}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setOpenPaymentDialog(false)}>キャンセル</Button>
                <Button onClick={handleCreatePayment} variant="contained" color="primary">
                  登録
                </Button>
              </DialogActions>
            </Dialog>
          </Paper>
        )}

        {/* --- 契約 --- */}
        {tab === 4 && (
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>契約一覧</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>契約名</TableCell>
                  <TableCell>締結日</TableCell>
                  <TableCell>ステータス</TableCell>
                  <TableCell>契約書</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contracts.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>{c.title || '-'}</TableCell>
                    <TableCell>{c.signedDate ? dayjs(c.signedDate).format('YYYY/MM/DD') : '-'}</TableCell>
                    <TableCell>{c.status || '-'}</TableCell>
                    <TableCell>
                      {c.fileUrl ? (
                        <Link href={c.fileUrl} target="_blank" rel="noopener noreferrer">
                          PDFを開く
                        </Link>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}

        {/* --- ファイル --- */}
        {tab === 5 && (
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="h6">ファイル一覧</Typography>
              <Button variant="contained" component="label">
                ファイル追加
                <input hidden type="file" onChange={handleUploadFile} />
              </Button>
            </Box>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ファイル名</TableCell>
                  <TableCell>種類</TableCell>
                  <TableCell>登録者</TableCell>
                  <TableCell>登録日</TableCell>
                  <TableCell align="center">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.isArray(files) && files.length > 0 ? (
                  files.map((f) => (
                    <TableRow key={f.id}>
                      <TableCell>
                        <Link
                          component="button"
                          onClick={async () => {
                            try {
                              const { data } = await api.get(`/api/files/${f.id}/url`);
                              if (data?.signedUrl) window.open(data.signedUrl, '_blank');
                              else alert('ファイルURLの取得に失敗しました');
                            } catch (err) {
                              console.error('[File open error]', err);
                              alert('ファイルを開けませんでした');
                            }
                          }}
                        >
                          {f.fileName || '(名称なし)'}
                        </Link>
                      </TableCell>
                      <TableCell>{f.fileType?.toUpperCase() || '-'}</TableCell>
                      <TableCell>{f.uploadedBy || '-'}</TableCell>
                      <TableCell>{f.uploadedAt ? dayjs(f.uploadedAt).format('YYYY/MM/DD HH:mm') : '-'}</TableCell>
                      <TableCell align="center">
                        <Button color="error" size="small" onClick={() => setDeleteDialog(f)}>
                          削除
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary' }}>
                      ファイルが登録されていません
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        )}

        {/* --- 発行履歴 (xlsx 行18 + Batch 2.11: Log書き込みベース、PDF発行ごとに1行) --- */}
        {tab === 6 && (
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>発行履歴</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>種別</TableCell>
                  <TableCell>発行日時</TableCell>
                  <TableCell>番号</TableCell>
                  <TableCell>発行者</TableCell>
                  <TableCell align="right">金額</TableCell>
                  <TableCell>ステータス</TableCell>
                  <TableCell align="center">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((h) => (
                  <TableRow key={h.logId || `${h.type}-${h.docId}`}>
                    <TableCell>{h.type}</TableCell>
                    <TableCell>{h.operatedAt ? dayjs(h.operatedAt).format('YYYY/MM/DD HH:mm') : '-'}</TableCell>
                    <TableCell>{h.docNo || '-'}</TableCell>
                    <TableCell>{h.operatedByName || '-'}</TableCell>
                    <TableCell align="right">¥{(h.amount || 0).toLocaleString()}</TableCell>
                    <TableCell>
                      <Chip
                        label={h.status || '-'}
                        color={
                          h.status === '入金済' || h.status === '支払済' || h.status === '承認済'
                            ? 'success'
                            : h.status === '発行済' || h.status === '稟議中'
                              ? 'info'
                              : 'default'
                        }
                        size="small"
                      />
                    </TableCell>

                    {/* 操作 */}
                    <TableCell align="center">
                      {h.type === "請求書" ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                          <Button size="small" variant="outlined" onClick={() => handleEditInvoice(h.id)} disabled={reviseLoading}>
                            修正
                          </Button>
                          <Button size="small" variant="contained" onClick={() => handleCopyInvoice(h.id)}>
                            複製
                          </Button>
                        </Box>
                      ) : h.type === "見積書" ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                          <Button size="small" variant="outlined" onClick={() => handleEditEstimate(h.id)}>
                            修正
                          </Button>
                          <Button size="small" variant="contained" onClick={() => handleCopyEstimate(h.id)}>
                            複製
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            color="error"
                            onClick={() => handleDeleteEstimate(h.id)}
                          >
                            削除
                          </Button>
                        </Box>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}

        {tab === 7 && <ProjectChat projectId={project.id} user={user} />}

        <Dialog open={!!deleteDialog} onClose={() => setDeleteDialog(null)}>
          <DialogTitle>
            {deleteDialog ? `${deleteDialog.originalName || deleteDialog.name} を削除しますか？` : ''}
          </DialogTitle>
          <DialogActions>
            <Button onClick={() => setDeleteDialog(null)}>キャンセル</Button>
            <Button color="error" variant="contained" onClick={handleDeleteFile}>
              削除
            </Button>
          </DialogActions>
        </Dialog>

        {/* 請求書編集（revise） */}
        {openInvoiceEdit && editingInvoice && (
          <InvoiceCreateDialog
            open={openInvoiceEdit}
            onClose={() => {
              setOpenInvoiceEdit(false);
              setEditingInvoice(null);
            }}
            customer={{
              id: editingInvoice.customerId ?? project?.customerId,
              name: editingInvoice.customerName || project?.customerName || "",
            }}
            mode="edit"
            initialData={editingInvoice}
            onSubmit={async () => {
              setOpenInvoiceEdit(false);
              setEditingInvoice(null);
              await loadHistory();
            }}
          />
        )}

        {/* 請求書新規（案件起点） / 請求書複製（履歴起点） */}
        {openInvoice && (
          <InvoiceCreateDialog
            open={openInvoice}
            mode="create"
            customer={invoiceCustomer}
            initialData={{
              projectId: invoiceProjectId,
              ...(invoiceInitialData || {}),
            }}
            onClose={() => {
              setOpenInvoice(false);
              setInvoiceCustomer(null);
              setInvoiceProjectId(null);
              setInvoiceInitialData(null);
            }}
            onSubmit={async () => {
              // 登録と PDF 発行はダイアログ側で完了している
              setOpenInvoice(false);
              setInvoiceCustomer(null);
              setInvoiceProjectId(null);
              setInvoiceInitialData(null);
              await loadHistory();
            }}
          />
        )}

        {/* 納品書発行（発行日を任意指定、xlsx 行92） */}
        <Dialog open={openDelivery} onClose={() => setOpenDelivery(false)} fullWidth maxWidth="xs">
          <DialogTitle>納品書発行</DialogTitle>
          <DialogContent>
            <TextField
              label="発行日"
              type="date"
              fullWidth
              margin="dense"
              value={deliveryForm.issueDate}
              onChange={(e) => setDeliveryForm((f) => ({ ...f, issueDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="金額"
              type="number"
              fullWidth
              margin="dense"
              value={deliveryForm.amount}
              onChange={(e) => setDeliveryForm((f) => ({ ...f, amount: e.target.value }))}
            />
            <TextField
              label="備考"
              fullWidth
              margin="dense"
              multiline
              minRows={2}
              value={deliveryForm.note}
              onChange={(e) => setDeliveryForm((f) => ({ ...f, note: e.target.value }))}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDelivery(false)}>キャンセル</Button>
            <Button variant="contained" onClick={handleCreateDelivery} disabled={deliveryLoading}>
              {deliveryLoading ? '発行中...' : '発行してPDFを取得'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* 見積書編集（revise） */}
        {openEstimateEdit && editingEstimate && (
          <EstimateCreateDialog
            open={openEstimateEdit}
            onClose={() => {
              setOpenEstimateEdit(false);
              setEditingEstimate(null);
            }}
            customer={{
              id: editingEstimate.customerId ?? project?.customerId,
              name: editingEstimate.customerName || project?.customerName || "",
            }}
            mode="edit"
            initialData={editingEstimate}
            onSubmit={async () => {
              setOpenEstimateEdit(false);
              setEditingEstimate(null);
              await loadHistory();
            }}
          />
        )}

        {/* 見積書新規（案件起点） / 見積書複製（履歴起点） */}
        {openEstimate && (
          <EstimateCreateDialog
            open={openEstimate}
            mode="create"
            customer={estimateCustomer}
            initialData={{
              projectId: estimateProjectId,
              ...(estimateInitialData || {}),
            }}
            onClose={() => {
              setOpenEstimate(false);
              setEstimateCustomer(null);
              setEstimateProjectId(null);
              setEstimateInitialData(null);
            }}
            onSubmit={async () => {
              // 登録と PDF 発行はダイアログ側で完了している (xlsx 行89)
              setOpenEstimate(false);
              setEstimateCustomer(null);
              setEstimateProjectId(null);
              setEstimateInitialData(null);
              await loadHistory();
            }}

          />
        )}

      </Box>
    </PageContainer>
  );
}
